'use client';

import { useState } from 'react';
import { isEmpty } from 'lodash';
import Heading from '../Heading/Heading';
import { getTruncatedContent, HTML_TAG_PATTERN, MAX_LENGTH } from '~/clientUtils/truncateContent';
import SafeHTML from '../SafeHTML/SafeHTML';

type Props = {
    heading: string;
    description?: string;
    electionLogoUrl: string;
};

const ElectionInfo = (props: Readonly<Props>) => {
    const { heading, description = '', electionLogoUrl } = props;
    const [isExpanded, setIsExpanded] = useState(false);

    const containerClassName = 'info px-[15px] pb-[30px]';
    const descriptionClassName = 'description';
    const toggleButtonClassName = 'ml-1 inline rounded-sm text-secondary';
    const mobileViewClassName = 'md:hidden';
    const desktopViewClassName = 'hidden md:block';

    const textContent = description.replace(HTML_TAG_PATTERN, '').trim();
    const shouldTruncate = textContent.length > MAX_LENGTH;

    const truncatedContent = getTruncatedContent({
        description,
        isExpanded,
        shouldTruncate,
    });

    if (isEmpty(description)) {
        return (
            <div className={containerClassName}>
                <Heading title={heading} electionLogoUrl={electionLogoUrl} />
            </div>
        );
    }

    const handleToggle = (): void => {
        setIsExpanded((prev) => !prev);
    };

    return (
        <div className={containerClassName}>
            <Heading title={heading} electionLogoUrl={electionLogoUrl} />
            <div>
                {/* Mobile view with truncation */}
                <div className={mobileViewClassName}>
                    <div className={descriptionClassName}>
                        <SafeHTML tag="span" html={truncatedContent} />
                        {shouldTruncate && !isExpanded && <span>...</span>}
                        {shouldTruncate && (
                            <button
                                onClick={handleToggle}
                                className={toggleButtonClassName}
                                aria-expanded={isExpanded}
                                type="button"
                            >
                                {isExpanded ? 'se mindre' : 'les mer'}
                            </button>
                        )}
                    </div>
                </div>

                {/* Desktop view - always full text */}
                <div className={desktopViewClassName}>
                    <SafeHTML className={descriptionClassName} html={description} />
                </div>
            </div>
        </div>
    );
};

export default ElectionInfo;
